/**
 * ADMIN MODULE — email template (branding chrome) data access.
 *
 * One platform-level template wraps every transactional email Brand Command
 * sends to customers (header + footer around each email's content card). RLS
 * pins email_template_settings to platform admins; components call these helpers
 * instead of touching supabase directly. Errors are logged here and surfaced
 * generic. Writes filter caller fields through an explicit ALLOWED set.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

const HEX_RE     = /^#[0-9a-fA-F]{6}$/;
const HTTP_RE    = /^https?:\/\//i;
const PLACEMENTS = ["left", "center", "right"];
const EMAIL_TYPE = "internal"; // single template for now (table is keyed for more)

export const TEMPLATE_DEFAULTS = {
  header_bg_color: "#111827", header_text: "", header_logo_url: "", header_logo_placement: "left",
  footer_bg_color: "#f8fafc", footer_text: "", footer_logo_url: "", footer_logo_placement: "center",
};

const SELECT_COLS =
  "id, header_bg_color, header_text, header_logo_url, header_logo_placement, " +
  "footer_bg_color, footer_text, footer_logo_url, footer_logo_placement, updated_at";

export async function loadEmailTemplate() {
  const { data, error } = await supabase
    .from("email_template_settings")
    .select(SELECT_COLS)
    .eq("email_type", EMAIL_TYPE)
    .maybeSingle();
  if (error) {
    console.error("[emailService.loadEmailTemplate]", error);
    throw new Error("Could not load the email template.");
  }
  // Coalesce nulls to empty strings so the form is always controlled.
  const row = data || {};
  const out = { ...TEMPLATE_DEFAULTS };
  for (const k of Object.keys(TEMPLATE_DEFAULTS)) {
    if (row[k] != null) out[k] = row[k];
  }
  out.updated_at = row.updated_at ?? null;
  return out;
}

const ALLOWED = new Set([
  "header_bg_color", "header_text", "header_logo_url", "header_logo_placement",
  "footer_bg_color", "footer_text", "footer_logo_url", "footer_logo_placement",
]);

export async function saveEmailTemplate(patch) {
  const out = {};
  for (const k of ALLOWED) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }

  const errors = [];
  for (const k of ["header_bg_color", "footer_bg_color"]) {
    if (k in out && !HEX_RE.test(out[k] || "")) errors.push("Colors must be #RRGGBB.");
  }
  for (const k of ["header_logo_placement", "footer_logo_placement"]) {
    if (k in out && !PLACEMENTS.includes(out[k])) errors.push("Invalid logo placement.");
  }
  for (const k of ["header_logo_url", "footer_logo_url"]) {
    if (k in out && out[k] && !HTTP_RE.test(out[k])) errors.push("Logo URL must start with http(s)://.");
  }
  for (const k of ["header_text", "footer_text"]) {
    if (k in out && out[k] != null && String(out[k]).length > 2000) errors.push("Text is too long (2000 max).");
  }
  if (errors.length) throw new Error(errors.join(" "));

  // Empty optional fields persist as NULL, not "".
  for (const k of ["header_logo_url", "footer_logo_url", "header_text", "footer_text"]) {
    if (k in out && out[k] === "") out[k] = null;
  }

  if (Object.keys(out).length === 0) return;

  const { error } = await supabase
    .from("email_template_settings")
    .update(out)
    .eq("email_type", EMAIL_TYPE);
  if (error) {
    console.error("[emailService.saveEmailTemplate]", error);
    throw new Error("Could not save the email template.");
  }
}
